import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useUser } from "../../Context/UserContext";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Typography,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

const DistrictSaplingTable = () => {
  const [reportData, setReportData] = useState([]);
  const [allDistricts, setAllDistricts] = useState([]);
  const [loading, setLoading] = useState(true);

  const { token } = useUser();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedSapling, setSelectedSapling] = useState(null);

  //  MAIN API CALLS
  useEffect(() => {
    if (!token) return;

    setLoading(true);

    // 1️⃣ TABLE DATA API
    axios
      .get("/api/center/district-sapling-summary/", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.saplings) {
          const processed = res.data.saplings.map((item, index) => ({
            id: index + 1,
            sno: index + 1,
            ...item,
          }));
          setReportData(processed);
        }
      })
      .catch((err) => console.error("Table API error:", err))
      .finally(() => setLoading(false));

    // 2️⃣ DISTRICT DROPDOWN API ✅
    axios
      .get("api/master/districts", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const districts = res.data.map((d) => ({
          id: String(d.id),
          name: d.District_Name,
        }));

        setAllDistricts(districts);
      })
      .catch((err) => console.error("District API error:", err));
  }, [token]);

  // 🔹 SAPLING OPTIONS
  const saplingOptions = useMemo(() => {
    return [...new Set(reportData.map((r) => r.sapling_name))];
  }, [reportData]);

  // 🔹 FILTER LOGIC
  const filteredRows = useMemo(() => {
    return reportData.filter((row) => {
      const matchesSearch = row.sapling_name
        ?.toLowerCase()
        .includes(searchText.toLowerCase());

      const matchesSapling =
        !selectedSapling || row.sapling_name === selectedSapling;

      const matchesDistrict =
        !selectedDistrict ||
        (row.district_names || []).some(
          (d) => String(d.id) === String(selectedDistrict.id),
        );

      return matchesSearch && matchesSapling && matchesDistrict;
    });
  }, [reportData, searchText, selectedSapling, selectedDistrict]);

  // 🔹 TABLE COLUMNS
  const columns = [
    { field: "sno", headerName: "S.No" },
    {
      field: "sapling_name",
      headerName: "Sapling Name",
      flex: 1,
    },

    { field: "total_stock", headerName: "Opening stock", flex: 1 },
    { field: "total_sold", headerName: "Sold", flex: 1 },

    {
      field: "in_hand",
      headerName: "In Hand",
      flex: 1,
      valueGetter: (value, row) => {
        const stock = Number(row.total_stock) || 0;
        const sold = Number(row.total_sold) || 0;
        return stock - sold;
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      {/* 🔍 FILTER BAR */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* ✅ DISTRICT DROPDOWN */}
        <Autocomplete
          size="small"
          options={allDistricts}
          getOptionLabel={(o) => o.name || ""}
          isOptionEqualToValue={(o, v) => o.id === v?.id}
          value={selectedDistrict}
          onChange={(e, val) => setSelectedDistrict(val)}
          renderInput={(params) => (
            <TextField {...params} label="Select District" />
          )}
          sx={{ width: 220 }}
        />

        {/* SAPLING DROPDOWN */}
        <Autocomplete
          size="small"
          options={saplingOptions}
          value={selectedSapling}
          onChange={(e, val) => setSelectedSapling(val)}
          renderInput={(params) => (
            <TextField {...params} label="Select Sapling" />
          )}
          sx={{ width: 200 }}
        />

        <Button
          onClick={() => {
            setSelectedDistrict(null);
            setSelectedSapling(null);
            setSearchText("");
          }}
        >
          Reset
        </Button>
      </Box>

      {/* 📊 TABLE */}
      <DataGrid
        rows={filteredRows}
        columns={columns}
        loading={loading}
        autoHeight
        slots={{ toolbar: GridToolbar }}
      />
    </Box>
  );
};

export default DistrictSaplingTable;
